import React from 'react'
import { Link } from 'react-router-dom'
import { Mail, Phone, MapPin, Clock } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import {
  SUPPORT_EMAIL,
  PRIVACY_EMAIL,
  COMPANY_NAME,
  DBA_NAME,
  WEBSITE_URL,
  SUPPORT_PHONE,
  SUPPORT_PHONE_TEL,
  COMPANY_ADDRESS_LINE1,
  COMPANY_ADDRESS_LINE2,
  SUPPORT_HOURS,
} from '../constants/contact'

export default function Contact() {
  const { user } = useAuth()

  return (
    <div className="min-h-screen bg-paybyphone-light">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to={user ? '/dashboard' : '/'} className="text-xl font-bold text-paybyphone-green hover:text-paybyphone-green/80 transition-colors">
            {DBA_NAME}
          </Link>
          {user && (
            <Link to="/dashboard" className="text-paybyphone-green hover:text-paybyphone-green/80">
              ← Back to Dashboard
            </Link>
          )}
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Contact Us</h1>
          <p className="text-gray-600 mb-8">
            Reach out to our team — we typically respond within one business day.
          </p>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Phone */}
            <div className="border border-gray-200 rounded-xl p-6">
              <div className="flex items-center mb-3">
                <Phone className="w-5 h-5 text-paybyphone-green mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Phone</h2>
              </div>
              <a
                href={`tel:${SUPPORT_PHONE_TEL}`}
                className="text-paybyphone-green text-xl font-medium hover:underline"
              >
                {SUPPORT_PHONE}
              </a>
              <p className="text-sm text-gray-500 mt-2">{SUPPORT_HOURS}</p>
            </div>

            {/* Email — Support */}
            <div className="border border-gray-200 rounded-xl p-6">
              <div className="flex items-center mb-3">
                <Mail className="w-5 h-5 text-paybyphone-green mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Customer Support</h2>
              </div>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-paybyphone-green text-lg font-medium hover:underline break-all"
              >
                {SUPPORT_EMAIL}
              </a>
              <p className="text-sm text-gray-500 mt-2">
                Subscription, billing, refunds, technical issues.
              </p>
            </div>

            {/* Email — Privacy */}
            <div className="border border-gray-200 rounded-xl p-6">
              <div className="flex items-center mb-3">
                <Mail className="w-5 h-5 text-paybyphone-green mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Privacy Requests</h2>
              </div>
              <a
                href={`mailto:${PRIVACY_EMAIL}`}
                className="text-paybyphone-green text-lg font-medium hover:underline break-all"
              >
                {PRIVACY_EMAIL}
              </a>
              <p className="text-sm text-gray-500 mt-2">
                Data access, deletion, and CCPA-related requests.
              </p>
            </div>

            {/* Mailing Address */}
            <div className="border border-gray-200 rounded-xl p-6">
              <div className="flex items-center mb-3">
                <MapPin className="w-5 h-5 text-paybyphone-green mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Mailing Address</h2>
              </div>
              <address className="not-italic text-gray-700 leading-relaxed">
                {COMPANY_NAME}<br />
                d/b/a {DBA_NAME}<br />
                {COMPANY_ADDRESS_LINE1}<br />
                {COMPANY_ADDRESS_LINE2}
              </address>
            </div>
          </div>

          {/* Business hours */}
          <div className="mt-6 border border-gray-200 rounded-xl p-6 flex items-start">
            <Clock className="w-5 h-5 text-paybyphone-green mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Business Hours</h2>
              <p className="text-gray-700">{SUPPORT_HOURS}</p>
              <p className="text-sm text-gray-500 mt-1">
                Closed on U.S. federal holidays.
              </p>
            </div>
          </div>

          {/* Legal disclosure */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              {DBA_NAME} is a registered trade name of {COMPANY_NAME}, a Limited Liability Company organized under
              the laws of the United States. For legal notices, refer to our{' '}
              <Link to="/terms" className="text-paybyphone-green underline">Terms of Service</Link>,{' '}
              <Link to="/privacy" className="text-paybyphone-green underline">Privacy Policy</Link>, and{' '}
              <Link to="/refund" className="text-paybyphone-green underline">Refund Policy</Link>. Website: {WEBSITE_URL}.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
